import { useEffect, useSyncExternalStore, useRef, useState, useCallback } from 'react';
import {
  subscribe,
  getSnapshot,
  update,
  hydrate,
  reset,
} from '@/lib/positionAnimator';
import { isStationaryFix } from '@trackizy/formatters';

const LS_KEY = 'trackizy_livemap_animate_positions';

/**
 * Live snapshot of animated positions, keyed by deviceId.
 * Re-renders at the engine's emit rate (15 Hz) while any leg is in flight,
 * then idles until the next WS fix kicks the engine.
 *
 * @returns {ReadonlyMap<number, object>}
 */
export function useAnimatedPositions() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Feeds the Redux positions slice into the engine whenever it changes.
 * Idempotent — out-of-order fixes are dropped by the engine itself.
 *
 * When `enabled` flips false → true, hydrates from current Redux to snap
 * every marker to its present location (no "glide in" from a stale state).
 * When it flips true → false, resets the engine so memory doesn't grow
 * while the feature is off.
 *
 * @param {Record<string|number, object>} positions  Redux state.session.positions
 * @param {boolean} enabled
 */
export function useFeedAnimator(positions, enabled) {
  const wasEnabledRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      if (wasEnabledRef.current) reset();
      wasEnabledRef.current = false;
      return;
    }

    if (!wasEnabledRef.current) {
      // First feed after a toggle-on (or first mount): snap everything.
      hydrate(Object.values(positions || {}));
      wasEnabledRef.current = true;
      return;
    }

    for (const p of Object.values(positions || {})) {
      // Stationary fixes (parked vehicle + GPS jitter) are dropped here
      // rather than passed to the engine. The animator's last leg finishes
      // naturally and the marker freezes at the last truly-moving target.
      // This also keeps the trail tail-substitution glued to that frozen
      // point instead of zigzagging through the jitter cloud.
      if (isStationaryFix(p)) continue;
      update(p);
    }
  }, [positions, enabled]);

  // Reset on unmount only if we were actively feeding — leaving stale
  // state behind would surface as "animation from the wrong start point"
  // when the next session opens Live Map.
  useEffect(
    () => () => {
      if (wasEnabledRef.current) reset();
    },
    [],
  );
}

/**
 * Per-user toggle for the inter-fix animation. localStorage-backed; no
 * server persistence (pure display preference, like density / column vis).
 *
 * @returns {[boolean, () => void]}
 */
export function useLiveMapAnimate() {
  const [enabled, setEnabled] = useState(() => {
    try {
      const v = localStorage.getItem(LS_KEY);
      return v == null ? true : v === 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, String(enabled));
    } catch {
      /* private-mode safari etc. — non-fatal */
    }
  }, [enabled]);

  const toggle = useCallback(() => setEnabled((p) => !p), []);

  return [enabled, toggle];
}
